import { divInt, isqrt, sub, toPixels } from "../shared/fixed";
import type { AbilityDef, Box, HitSpec, MatchRules } from "./ability";
import type { SimEvent } from "./events";
import { reactionLean } from "./reaction";
import { setState } from "./transition";
import { BACK_HIT_DENOMINATOR, BACK_HIT_NUMERATOR } from "./turn";
import { BUTTON } from "./input";
import { FIGHTER, type FighterState, type FighterStateId, type MatchState } from "./state";

export const METER_MAX = 3000;
/** Сколько ударов можно добавить сбитому в воздухе: дальше он неуязвим до земли — бесконечных жонглирований нет. */
export const MAX_JUGGLE = 3;

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Зона из данных (пиксели, лицом вправо) → прямоугольник мира в подпикселях. */
export function worldRect(fighter: FighterState, box: Box): Rect {
  const near = fighter.facing > 0 ? box.x : -box.x - box.w;
  return {
    left: fighter.x + sub(near),
    right: fighter.x + sub(near + box.w),
    top: fighter.y + sub(box.y),
    bottom: fighter.y + sub(box.y + box.h),
  };
}

const overlaps = (a: Rect, b: Rect): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/** Зона попаданий по состоянию; null — неуязвим (лежит, встаёт, повержен). */
/**
 * Зона попадания бойца. В отдаче и в полёте она идёт за телом клипа реакции (консультант 05.11.2026: «тело для
 * попаданий идёт за анимацией»): сдвиг корпуса по кадру реакции из таблицы героя (обмер клипов, целые пиксели —
 * детерминированно, онлайн одинаково).
 */
export function hurtbox(fighter: FighterState, rules: MatchRules, side: 0 | 1): Box | null {
  const box = baseHurtbox(fighter, rules, side);
  if (!box) return null;
  const lean = reactionLean(fighter, rules.heroes[side].reactionBody);
  return lean === 0 ? box : { ...box, x: box.x + lean };
}

function baseHurtbox(fighter: FighterState, rules: MatchRules, side: 0 | 1): Box | null {
  const hero = rules.heroes[side];
  // скачок-пыльца: пока тело — облако частиц, попасть нельзя
  if (fighter.state === FIGHTER.ability) {
    const vanish = rules.kits[side][fighter.ability]?.vanish;
    if (vanish && fighter.abilityFrame >= vanish[0] && fighter.abilityFrame < vanish[1]) return null;
  }
  switch (fighter.state) {
    case FIGHTER.knockdown:
    case FIGHTER.getup:
    case FIGHTER.ko:
    case FIGHTER.victory:
    case FIGHTER.thrown:
      return null;
    case FIGHTER.crouch:
    case FIGHTER.crouchBlock:
      return hero.hurtCrouch;
    case FIGHTER.airHit:
      return fighter.juggle >= MAX_JUGGLE ? null : hero.hurtAir;
    case FIGHTER.jump:
      return hero.hurtAir;
    default:
      return fighter.y < 0 ? hero.hurtAir : hero.hurtStand;
  }
}

export function activeAbility(fighter: FighterState, rules: MatchRules, side: 0 | 1): AbilityDef | null {
  if (fighter.state !== FIGHTER.ability || fighter.hasHit) return null;
  const ability = rules.kits[side][fighter.ability];
  if (!ability?.hitbox || !ability.hit) return null;
  const frame = fighter.abilityFrame - 1;
  return frame >= ability.startup && frame < ability.startup + ability.active ? ability : null;
}

/** Доля урона, которая становится серой (можно отыграть), и доля нанесённого урона, что отыгрывает своё серое, %. */
const GREY_SHARE = 40;
const REGAIN_SHARE = 60;
/** Последний шанс: здоровье не больше этой доли, % — Ярость копится в LAST_CHANCE_BOOST / 10 раза быстрее. */
export const LAST_CHANCE_PERCENT = 20;
const LAST_CHANCE_BOOST = 15;

/** Из этих состояний удержание блока закрывает удар в тот же кадр. */
const GUARD_STATES: readonly FighterStateId[] = [
  FIGHTER.block,
  FIGHTER.crouchBlock,
  FIGHTER.blockstun,
  FIGHTER.idle,
  FIGHTER.walkBack,
  FIGHTER.crouch,
];

function blocks(defender: FighterState, hit: HitSpec): boolean {
  const holding = (defender.lastBits & BUTTON.block) !== 0;
  if (!holding) return false;
  // в прыжке блок держит всё, кроме подброса — обмен ударами на лету
  if (defender.y < 0) return defender.state === FIGHTER.jump && hit.launch === undefined;
  const crouching = (defender.lastBits & BUTTON.down) !== 0;
  const guarding = GUARD_STATES.includes(defender.state);
  if (!guarding) return false;
  return crouching ? hit.height !== "overhead" : hit.height !== "low";
}

export interface Contact {
  attacker: 0 | 1;
  ability: AbilityDef;
  hit: HitSpec;
  x: number;
  y: number;
  /** Куда отбрасывает: у удара — взгляд атакующего, у снаряда — его полёт. */
  direction: number;
  /** Попал снаряд: владелец в это время может быть занят другим, его удар не помечается попавшим. */
  shot?: boolean;
}

function findContact(state: MatchState, rules: MatchRules, side: 0 | 1): Contact | null {
  const attacker = state.fighters[side];
  const defenderSide: 0 | 1 = side === 0 ? 1 : 0;
  const defender = state.fighters[defenderSide];
  const ability = activeAbility(attacker, rules, side);
  const hurt = hurtbox(defender, rules, defenderSide);
  if (!ability?.hitbox || !ability.hit || !hurt) return null;
  const strike = worldRect(attacker, ability.hitbox);
  const body = worldRect(defender, hurt);
  if (!overlaps(strike, body)) return null;
  const x = toPixels((Math.max(strike.left, body.left) + Math.min(strike.right, body.right)) / 2);
  const y = toPixels((Math.max(strike.top, body.top) + Math.min(strike.bottom, body.bottom)) / 2);
  return { attacker: side, ability, hit: ability.hit, x, y, direction: attacker.facing };
}

const strengthOf = (hit: HitSpec): number => (hit.hitstop <= 7 ? 0 : hit.hitstop <= 11 ? 1 : 2);

export function applyContact(state: MatchState, rules: MatchRules, contact: Contact, events: SimEvent[]): void {
  const meter = rules.meter;
  const attacker = state.fighters[contact.attacker];
  const defender = state.fighters[contact.attacker === 0 ? 1 : 0];
  const { hit, ability, direction } = contact;
  if (!contact.shot) attacker.hasHit = 1;
  // удар в спину: защитник смотрит туда же, куда летит удар (стоит спиной) — блок не держит, урон ×1,5
  const back = defender.facing === direction && defender.y >= 0;
  if (!back && blocks(defender, hit)) {
    defender.health = Math.max(1, defender.health - (hit.chip ?? 0));
    setState(defender, FIGHTER.blockstun);
    defender.stun = hit.blockstun;
    // в блоке отбрасывает вдвое слабее, чем на попадании: защита держит место, а не уезжает через арену
    defender.vx = divInt(sub(hit.push), 2) * direction;
    attacker.meter = Math.min(METER_MAX, attacker.meter + divInt(hit.damage * meter.blockAttacker * 10, 10));
    defender.meter = Math.min(METER_MAX, defender.meter + divInt(hit.damage * meter.blockDefender * 10, 10));
    if (rules.hitstop) state.hitstop = Math.max(state.hitstop, divInt(hit.hitstop, 2));
    events.push({
      kind: "block",
      frame: state.frame,
      who: contact.attacker,
      x: contact.x,
      y: contact.y,
      strength: strengthOf(hit),
      ability: ability.id,
      ...(ability.fx?.block ? { fx: ability.fx.block } : {}),
    });
    return;
  }
  // куда и как сильно попали — по этому тело отвечает (render: голова или корпус, сила рывка)
  defender.hurtSpot = hit.height === "high" ? 1 : hit.height === "low" ? 3 : 2;
  defender.hurtPower = Math.min(2, strengthOf(hit) + (back ? 1 : 0));
  const scale = Math.max(30, 100 - defender.juggle * 15);
  const base = back ? divInt(hit.damage * BACK_HIT_NUMERATOR, BACK_HIT_DENOMINATOR) : hit.damage;
  const damage = divInt(base * scale, 100);
  defender.health = Math.max(0, defender.health - damage);
  // серое здоровье: прежнее серое у пропустившего сгорает, часть нового урона становится серой; атакующий своим
  // попаданием отыгрывает своё серое обратно в здоровье
  defender.grey = defender.health > 0 ? divInt(damage * GREY_SHARE, 100) : 0;
  const regained = Math.min(attacker.grey, divInt(damage * REGAIN_SHARE, 100));
  attacker.health += regained;
  attacker.grey -= regained;
  // последний шанс: на малом здоровье Ярость копится быстрее — путь к «Шквалу» и камбэку
  const boost = (fighter: FighterState, side: 0 | 1): number =>
    fighter.health * 100 <= rules.heroes[side].health * LAST_CHANCE_PERCENT ? LAST_CHANCE_BOOST : 10;
  const attackerSide = contact.attacker;
  const defenderSide: 0 | 1 = attackerSide === 0 ? 1 : 0;
  attacker.meter = Math.min(
    METER_MAX,
    attacker.meter + divInt(hit.damage * meter.hitAttacker * boost(attacker, attackerSide), 10),
  );
  defender.meter = Math.min(
    METER_MAX,
    defender.meter + divInt(damage * meter.hitDefender * boost(defender, defenderSide), 10),
  );
  if (rules.hitstop) state.hitstop = Math.max(state.hitstop, hit.hitstop);
  const armored = defender.armor > 0 && defender.health > 0;
  if (armored) defender.armor--;
  else if (defender.health === 0 || hit.launch !== undefined || hit.knockdown || defender.y < 0) {
    setState(defender, defender.health === 0 ? FIGHTER.ko : FIGHTER.airHit);
    defender.ability = -1;
    // решающий нокаут в замедлении — отлёт назад, тяжёлое падение (консультант 05.11.2026: «нокаут от прямого читается
    // как „осел на корточки“»): какой бы удар ни добил
    const decisive = defender.health === 0 && state.slowmo > 0;
    // подсечка сбивает по-своему: споткнулся и кувыркнулся (Soccer Trip), а не отлетел от удара
    defender.fallKind = !decisive && hit.height === "low" && hit.knockdown === true ? 1 : 0;
    const gravity = rules.heroes[contact.attacker === 0 ? 1 : 0].gravity;
    const launch = decisive ? KO_DECISIVE_LAUNCH : (hit.launch ?? (defender.health === 0 ? 5 : 3));
    defender.vy = -launchSpeed(sub(launch), defender, gravity);
    defender.vx = sub(decisive ? KO_DECISIVE_PUSH : Math.max(1.5, hit.push)) * direction;
    defender.juggle++;
  } else {
    setState(defender, FIGHTER.hitstun);
    defender.ability = -1;
    defender.stun = hit.hitstun;
    defender.vx = sub(hit.push) * direction;
  }
  events.push({
    kind: "hit",
    frame: state.frame,
    who: contact.attacker,
    x: contact.x,
    y: contact.y,
    strength: Math.min(2, strengthOf(hit) + (back ? 1 : 0)),
    ability: ability.id,
    ...(ability.fx?.hit ? { fx: ability.fx.hit } : {}),
    ...(ability.sound?.hit ? { sound: ability.sound.hit } : {}),
  });
}

/**
 * Потолок подброса: стопы сбитого не выше MAX_AIR_HEIGHT над полом, голова остаётся под HUD (консультант 04.11.2026:
 * «апперкот — красный улетает почти под HUD»). Скорость вверх урезается так, чтобы высшая точка была не выше потолка.
 */
const MAX_AIR_HEIGHT = sub(150);

/** Отлёт при нокауте — не выше роста бойца (консультант 05.11.2026: «тело всё время в кадре»), пиксели. */
const KO_AIR_HEIGHT = sub(90);
/** Решающий нокаут: скорость отлёта вверх и назад, пикселей за кадр (вершина ~60 px, назад ~110 px). */
const KO_DECISIVE_LAUNCH = 11;
const KO_DECISIVE_PUSH = 5;

function launchSpeed(wanted: number, defender: FighterState, gravityPixels: number): number {
  const gravity = sub(gravityPixels);
  const ceiling = defender.health === 0 ? KO_AIR_HEIGHT : MAX_AIR_HEIGHT;
  const room = ceiling + defender.y; // y вверх отрицательный: сколько осталось до потолка
  return Math.min(wanted, isqrt(2 * gravity * Math.max(0, room)));
}

/** Попадания обоих считаются до применения: встречные удары бьют оба. */
export function resolveHits(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  const contacts = [findContact(state, rules, 0), findContact(state, rules, 1)];
  for (const contact of contacts) if (contact) applyContact(state, rules, contact, events);
}

/** Бойцы не расходятся дальше maxGap: камера держит обоих в кадре. Каждого тянет к другому поровну. */
function keepInView(state: MatchState, rules: MatchRules): void {
  const [a, b] = state.fighters;
  const excess = Math.abs(b.x - a.x) - sub(rules.arena.maxGap);
  if (excess <= 0) return;
  const toward = b.x > a.x ? 1 : -1;
  const half = divInt(excess + 1, 2);
  a.x += half * toward;
  b.x -= half * toward;
}

/** По вертикали тела перекрываются, пока разница высот меньше этой: перепрыгнуть можно только высоко над головой. */
const BODY_OVERLAP = sub(120);

/**
 * На бегу и в выпаде корпус наклонён вперёд: коробка тела вытягивается на столько к сопернику, чтобы позы не
 * сливались в одну фигуру (консультант 04.11.2026: «тела снова входят друг в друга при разгоне»).
 */
const LEAN = sub(16);

function lean(fighter: FighterState, rules: MatchRules, side: 0 | 1): number {
  // шаг продолжения серии (chase) — не рывок: тело не наклоняется, боец подходит к откинутому сопернику вплотную;
  // удар в выпаде тоже: его зона удара считается от стоп, наклон отодвинул бы её от тела (замер касания 05.11.2026)
  const striking = fighter.state === FIGHTER.ability && rules.kits[side][fighter.ability]?.hit !== undefined;
  const fast = fighter.chase === 0 && !striking && fighter.vx * fighter.facing > sub(rules.heroes[side].walkSpeed);
  const running = fighter.state === FIGHTER.walkForward && fighter.running > 0;
  return running || (fighter.state === FIGHTER.ability && fast) ? LEAN : 0;
}

/** Расталкивание тел: у каждого своя коробка тела, сквозь соперника не пройти, у края арены выдавливает другого. */
export function separate(state: MatchState, rules: MatchRules): void {
  const [a, b] = state.fighters;
  keepInView(state, rules);
  if (Math.abs(a.y - b.y) >= BODY_OVERLAP) return;
  // кувырок по полу проходит под соперником — тела не расталкиваются
  if (a.state === FIGHTER.knockdown || b.state === FIGHTER.knockdown) return;
  // тела расталкиваются там, где они есть: в отдаче корпус откинут — место освобождается, продолжение серии подходит
  // к откинутому телу (тот же сдвиг, что у зоны попадания, sim/reaction.ts); не ближе половины ширины
  const body = sub(reactionLean(a, rules.heroes[0].reactionBody)) + sub(reactionLean(b, rules.heroes[1].reactionBody));
  const minGap = Math.max(
    sub(rules.heroes[0].width / 2),
    sub((rules.heroes[0].width + rules.heroes[1].width) / 2) + lean(a, rules, 0) + lean(b, rules, 1) + body,
  );
  const gap = Math.abs(b.x - a.x);
  if (gap >= minGap) return;
  const direction = b.x > a.x || (b.x === a.x && a.facing > 0) ? 1 : -1;
  const push = divInt(minGap - gap + 1, 2);
  const left = sub(rules.arena.left);
  const right = sub(rules.arena.right);
  a.x -= push * direction;
  b.x += push * direction;
  const halfA = sub(rules.heroes[0].width / 2);
  const halfB = sub(rules.heroes[1].width / 2);
  if (a.x < left + halfA || a.x > right - halfA) {
    a.x = Math.min(Math.max(a.x, left + halfA), right - halfA);
    b.x = a.x + direction * minGap;
  }
  if (b.x < left + halfB || b.x > right - halfB) {
    b.x = Math.min(Math.max(b.x, left + halfB), right - halfB);
    a.x = b.x - direction * minGap;
  }
}
